import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";

export const Navbar = ({ t, lang, setLang }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "about", label: t.nav.about },
    { id: "services", label: t.nav.services },
    { id: "values", label: t.nav.values },
    { id: "vision", label: t.nav.vision },
    { id: "contact", label: t.nav.contact },
  ];

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-aa-green/90 backdrop-blur-xl border-b border-aa-gold/20 py-3" : "bg-transparent py-5"
      }`}
    >
      <nav className="mx-auto max-w-7xl px-5 sm:px-8 flex items-center justify-between">
        <button onClick={() => go("home")} data-testid="nav-home" className="shrink-0">
          <Logo className="h-9 sm:h-10" />
        </button>

        <div className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className="link-underline font-body text-sm tracking-wide text-aa-cream/85 hover:text-aa-gold transition-colors"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3 sm:gap-4">
          <LangToggle lang={lang} setLang={setLang} />
          <button
            data-testid="nav-cta"
            onClick={() => go("contact")}
            className="hidden sm:inline-flex font-body text-xs uppercase tracking-[0.18em] bg-aa-gold text-aa-green px-5 py-3 hover:bg-aa-cream transition-colors"
          >
            {t.nav.cta}
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden text-aa-cream p-1"
            aria-label="Menu"
          >
            {open ? <X size={24} strokeWidth={1.4} /> : <Menu size={24} strokeWidth={1.4} />}
          </button>
        </div>
      </nav>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-500 ${
          open ? "max-h-96 border-t border-aa-gold/20" : "max-h-0"
        }`}
      >
        <div className="bg-aa-green/95 backdrop-blur-xl px-6 py-6 flex flex-col gap-5">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-mobile-${l.id}`}
              onClick={() => go(l.id)}
              className="text-left font-heading text-lg text-aa-cream hover:text-aa-gold transition-colors"
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => go("contact")}
            className="font-body text-xs uppercase tracking-[0.18em] bg-aa-gold text-aa-green px-5 py-3 w-fit"
          >
            {t.nav.cta}
          </button>
        </div>
      </div>
    </header>
  );
};

const LangToggle = ({ lang, setLang }) => (
  <div data-testid="lang-toggle" className="flex items-center border border-aa-gold/30 text-xs font-body">
    {["fr", "en"].map((code) => (
      <button
        key={code}
        data-testid={`lang-${code}`}
        onClick={() => setLang(code)}
        className={`px-2.5 py-1.5 uppercase tracking-widest transition-colors ${
          lang === code ? "bg-aa-gold text-aa-green" : "text-aa-cream/70 hover:text-aa-gold"
        }`}
      >
        {code}
      </button>
    ))}
  </div>
);
